import React, { useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { SnackbarProvider, useSnackbar } from "notistack";
import { toString as errorsToString } from "../utils/parseErrors";

const NotificationContext = React.createContext(null);

const defaultAutoHideDuration = 3000;

const NotificationController = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const enqueueClosableSnackbar = useCallback(
    (message, options = {}) => {
      const { action: customAction, ...snackbarOptions } = options;

      enqueueSnackbar(message, {
        ...snackbarOptions,
        action: snackbarId => (
          <>
            {typeof customAction === "function" ? customAction(snackbarId) : customAction}
            <IconButton
              aria-label="dismiss notification"
              color="inherit"
              size="small"
              onClick={() => closeSnackbar(snackbarId)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ),
      });
    },
    [closeSnackbar, enqueueSnackbar]
  );

  const notifySuccess = useCallback(
    (message, options = {}) => {
      enqueueClosableSnackbar(message, {
        variant: "success",
        autoHideDuration: defaultAutoHideDuration,
        ...options,
      });
    },
    [enqueueClosableSnackbar]
  );

  const success = useCallback(
    ({ message, hide }) => {
      enqueueClosableSnackbar(message, {
        variant: "success",
        autoHideDuration: hide || defaultAutoHideDuration,
      });
    },
    [enqueueClosableSnackbar]
  );

  const notifyError = useCallback(
    error => {
      enqueueClosableSnackbar(errorsToString(error), {
        variant: "error",
        autoHideDuration: defaultAutoHideDuration,
      });
    },
    [enqueueClosableSnackbar]
  );

  return (
    <NotificationContext.Provider value={{ success, notifySuccess, notifyError }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const NotificationProvider = ({ children }) => {
  return (
    <SnackbarProvider
      maxSnack={1}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <NotificationController>{children}</NotificationController>
    </SnackbarProvider>
  );
};

export default NotificationContext;
